/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Chip,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  InputAdornment,
  Stack,
  SelectChangeEvent,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  CheckCircle,
  Cancel,
  Search,
  ShoppingCart,
  Visibility,
  AssignmentTurnedIn,
  HourglassTop,
  InfoOutlined,
  Clear,
  RemoveCircleOutline,
  Rule,
  FilterList,
  List,
  AddShoppingCart,
  ContentPasteSearch,
} from '@mui/icons-material';

import api from '../../../../../services/api';
import GenericSearchInput from '../../../../../components/common/GenericSearchInput';
import PurchaseRequestDetailsModal, {
  PurchaseRequestDetailData,
} from '../../../../../components/PurchaseRequestDetailsModal';
import ErpAuthModal from '../../../../../components/common/ErpAuthModal';

// --- INTERFACES ---
interface Branch {
  id: number;
  nome: string;
}

interface Company {
  id: number;
  nome: string;
  subtitulo: string;
  filiais: Branch[];
}

export interface PurchaseRequestItem extends PurchaseRequestDetailData {
  rotina: number;
  numAprovacao: number;
  nivelExigido: number;
  descricaoNivel: string;
  numProjeto?: number;
  descricaoProjeto?: string;
  faseProjeto?: number;
  descricaoFase?: string;
}

export interface ProcessApprovalResult {
  empresa: number;
  filial: number;
  solicitacao: number;
  sequencia: number;
  produtoOuServico?: string;
  descricao?: string;
  status: 'success' | 'error';
  message: string;
}

type SortableKeys =
  | 'solicitacao'
  | 'dataSolicitacao'
  | 'codUsuarioSolicitante'
  | 'descricaoProduto'
  | 'quantidadeSolicitada'
  | 'centroCusto'
  | 'situacaoAprovacao';

const STORAGE_KEY = 'purchase_request_filters';

const formatNumberPtBR = (num?: number) => {
  if (num === undefined || num === null || Number.isNaN(Number(num))) return '0';
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  }).format(Number(num));
};

function HighlightText({ text, highlight }: { text: string; highlight: string }) {
  const theme = useTheme();

  if (!highlight || !highlight.trim() || !text) {
    return <>{text}</>;
  }

  const tokens = highlight.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return <>{text}</>;

  const escapedTokens = tokens.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const regex = new RegExp(`(${escapedTokens.join('|')})`, 'gi');
  const parts = String(text).split(regex);

  return (
    <>
      {parts.map((part, index) => {
        const isMatch = tokens.some(t => t.toLowerCase() === part.toLowerCase());
        return isMatch ? (
          <Box
            key={index}
            component="span"
            sx={{
              bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.45 : 0.35),
              color: 'text.primary',
              fontWeight: 'bold',
              px: 0.3,
              borderRadius: '2px',
            }}
          >
            {part}
          </Box>
        ) : (
          part
        );
      })}
    </>
  );
}

export default function PurchaseRequestManager() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();

  const handleNavigate = (path: string) => {
    const target = path.startsWith('/') ? path : `/${path}`;
    if (typeof navigate === 'function') {
      navigate(target);
    } else {
      window.location.href = target;
    }
  };

  // 1. CAPTURA PARÂMETROS DA URL (LINK VINDO DO E-MAIL)
  const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const urlCompanyId = urlParams.get('companyId') ? Number(urlParams.get('companyId')) : null;
  const urlBranchId = urlParams.get('branchId') ? Number(urlParams.get('branchId')) : null;
  const urlRequestNum = urlParams.get('purchaseRequestNum') || urlParams.get('requestNum') || '';
  const urlStatus = urlParams.get('status') || '';

  const savedFilters = useMemo(() => {
    try {
      const item = localStorage.getItem(STORAGE_KEY);
      return item ? JSON.parse(item) : null;
    } catch {
      return null;
    }
  }, []);

  // Prioridade: URL -> LocalStorage -> Default
  const [companyId, setCompanyId] = useState<number>(urlCompanyId ?? savedFilters?.companyId ?? 1);
  const [branchId, setBranchId] = useState<number>(urlBranchId ?? savedFilters?.branchId ?? 1);
  const [statusFilter, setStatusFilter] = useState<string>(urlStatus || savedFilters?.statusFilter || 'ANA');
  const [purchaseRequestNum, setPurchaseRequestNum] = useState<string>(
    urlRequestNum || savedFilters?.purchaseRequestNum || '',
  );
  const [startDate, setStartDate] = useState<string>(savedFilters?.startDate ?? '');
  const [endDate, setEndDate] = useState<string>(savedFilters?.endDate ?? '');
  const [requesterInput, setRequesterInput] = useState<string>(savedFilters?.requesterInput ?? '');
  const [itemCodeInput, setItemCodeInput] = useState<string>(savedFilters?.itemCodeInput ?? '');
  const [quickSearch, setQuickSearch] = useState<string>('');

  const [requests, setRequests] = useState<PurchaseRequestItem[]>([]);
  const [companyList, setCompanyList] = useState<Company[]>([]);
  const [selectedItems, setSelectedItems] = useState<PurchaseRequestItem[]>([]);
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  const [sortField, setSortField] = useState<SortableKeys>('solicitacao');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState<boolean>(true);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  // ESTADOS PARA MOTIVO DE CANCELAMENTO / REPROVAÇÃO
  const [cancelReason, setCancelReason] = useState<string>('');
  const [cancelReasonError, setCancelReasonError] = useState<string>('');

  // MODAIS
  const [authModalOpen, setAuthModalOpen] = useState<boolean>(false);
  const [actionTarget, setActionTarget] = useState<{
    items: PurchaseRequestItem[];
    action: 'APR' | 'REP';
  } | null>(null);

  const [detailItem, setDetailItem] = useState<PurchaseRequestItem | null>(null);

  const [resultModal, setResultModal] = useState<{
    open: boolean;
    action: 'APR' | 'REP';
    results: ProcessApprovalResult[];
  }>({
    open: false,
    action: 'APR',
    results: [],
  });

  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    type: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    type: 'info',
  });

  const showNotification = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setNotification({ open: true, message, type });
  };

  useEffect(() => {
    const checkErpCredentials = async () => {
      try {
        const { data } = await api.get('erp/auth/status');
        if (!data || !data.authenticated) {
          showNotification('Atenção: Autentique-se no ERP Sapiens para continuar.', 'info');
          setAuthModalOpen(true);
        }
      } catch (error) {
        console.error('Erro ao checar autenticação ERP:', error);
        setAuthModalOpen(true);
      }
    };

    checkErpCredentials();
  }, []);

  const handleResetSearchData = () => {
    setRequests([]);
    setSelectedItems([]);
    setPage(0);
    setHasSearched(false);
  };

  const executeFilterSearch = async (
    cId = companyId,
    bId = branchId,
    status = statusFilter,
    reqNum = purchaseRequestNum,
  ) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          companyId: cId,
          branchId: bId,
          statusFilter: status,
          purchaseRequestNum: reqNum,
          startDate,
          endDate,
          requesterInput,
          itemCodeInput,
        }),
      );
    } catch (e) {
      console.error('Erro ao salvar filtros no localStorage:', e);
    }

    try {
      setIsLoading(true);
      setSelectedItems([]);
      const { data } = await api.get<PurchaseRequestItem[]>('erp/purchases/requests/approvals', {
        params: {
          companyId: cId,
          branchId: bId,
          routineId: 6,
          status,
          purchaseRequestNum: reqNum.trim() || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          requesters: requesterInput.trim() || undefined,
          itemCode: itemCodeInput.trim() || undefined,
        },
      });
      setRequests(data || []);
      setPage(0);
      setHasSearched(true);
    } catch (error: any) {
      console.error('Erro ao buscar solicitações:', error);
      const statusErr = error?.response?.status;
      const msg = error?.response?.data?.message || 'Erro ao carregar solicitações de compra.';

      if (
        statusErr === 401 ||
        msg.toLowerCase().includes('credenciais') ||
        msg.toLowerCase().includes('erp_login') ||
        msg.toLowerCase().includes('erp_password') ||
        msg.toLowerCase().includes('autentic')
      ) {
        showNotification('Autenticação do ERP necessária. Informe suas credenciais.', 'error');
        setAuthModalOpen(true);
      } else {
        showNotification(msg, 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchCompaniesAndInitialData = async () => {
      try {
        setIsLoadingCompanies(true);
        const { data } = await api.get<Company[]>('app/data-collection/get-company');
        if (data && Array.isArray(data) && data.length > 0) {
          setCompanyList(data);

          const targetCompanyId =
            urlCompanyId && data.some(c => c.id === urlCompanyId)
              ? urlCompanyId
              : savedFilters?.companyId && data.some(c => c.id === savedFilters.companyId)
                ? savedFilters.companyId
                : data[0].id;

          const foundCompany = data.find(c => c.id === targetCompanyId) || data[0];

          const targetBranchId =
            urlBranchId && foundCompany.filiais?.some(f => f.id === urlBranchId)
              ? urlBranchId
              : savedFilters?.branchId && foundCompany.filiais?.some(f => f.id === savedFilters.branchId)
                ? savedFilters.branchId
                : (foundCompany.filiais?.[0]?.id ?? 1);

          const targetStatus = urlStatus || statusFilter;
          const targetRequestNum = urlRequestNum || purchaseRequestNum;

          setCompanyId(targetCompanyId);
          setBranchId(targetBranchId);
          if (urlStatus) setStatusFilter(urlStatus);
          if (urlRequestNum) setPurchaseRequestNum(urlRequestNum);

          executeFilterSearch(targetCompanyId, targetBranchId, targetStatus, targetRequestNum);
        }
      } catch (error) {
        console.error('Erro ao carregar empresas:', error);
        showNotification('Erro ao carregar lista de empresas.', 'error');
      } finally {
        setIsLoadingCompanies(false);
      }
    };

    fetchCompaniesAndInitialData();
  }, []);

  const handleCompanyChange = (e: SelectChangeEvent<number>) => {
    const selectedCompanyId = Number(e.target.value);
    setCompanyId(selectedCompanyId);

    const foundCompany = companyList.find(emp => emp.id === selectedCompanyId);
    if (foundCompany && foundCompany.filiais && foundCompany.filiais.length > 0) {
      setBranchId(foundCompany.filiais[0].id);
    }
    handleResetSearchData();
  };

  const currentCompany = useMemo(() => {
    return companyList.find(e => e.id === companyId) || companyList[0] || { filiais: [] };
  }, [companyList, companyId]);

  const handleSortClick = (field: SortableKeys) => {
    const isAsc = sortField === field && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortField(field);
  };

  const getSortableDateTime = (dateStr?: string, timeNum?: number) => {
    if (!dateStr) return 0;

    let yyyymmdd = '';
    const trimmed = dateStr.trim();

    if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
      const [d, m, y] = trimmed.split('/');
      yyyymmdd = `${y}${m}${d}`;
    } else if (trimmed.includes('-')) {
      const clean = trimmed.split('T')[0];
      const [y, m, d] = clean.split('-');
      yyyymmdd = `${y}${m.padStart(2, '0')}${d.padStart(2, '0')}`;
    } else {
      yyyymmdd = trimmed;
    }

    const timeStr = String(timeNum ?? 0).padStart(4, '0');
    return Number(`${yyyymmdd}${timeStr}`);
  };

  const filteredRequests = useMemo(() => {
    let result = requests;

    if (quickSearch.trim()) {
      const term = quickSearch.toLowerCase();
      result = result.filter(r => {
        const sol = String(r.solicitacao);
        const prod = (r.descricaoProduto || r.descricaoServico || '').toLowerCase();
        const codProd = (r.produto || r.servico || '').toLowerCase();
        const cc = (r.descricaoCentroCusto || '').toLowerCase();
        const codCc = (r.centroCusto || '').toLowerCase();
        const solicitante = `${r.codUsuarioSolicitante} ${r.nomeUsuarioSolicitante || ''}`.toLowerCase();
        return (
          sol.includes(term) ||
          prod.includes(term) ||
          codProd.includes(term) ||
          cc.includes(term) ||
          codCc.includes(term) ||
          solicitante.includes(term)
        );
      });
    }

    return [...result].sort((a, b) => {
      let aVal: any = a[sortField] ?? '';
      let bVal: any = b[sortField] ?? '';

      if (sortField === 'dataSolicitacao') {
        aVal = getSortableDateTime(a.dataSolicitacao, a.horaSolicitacao);
        bVal = getSortableDateTime(b.dataSolicitacao, b.horaSolicitacao);
      } else if (sortField === 'descricaoProduto') {
        aVal = a.descricaoProduto || a.descricaoServico || '';
        bVal = b.descricaoProduto || b.descricaoServico || '';
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [requests, quickSearch, sortField, sortOrder]);

  const paginatedRequests = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRequests.slice(start, start + rowsPerPage);
  }, [filteredRequests, page, rowsPerPage]);

  const getItemKey = (item: PurchaseRequestItem) => `${item.empresa}-${item.solicitacao}-${item.sequencia}`;

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const selectable = filteredRequests.filter(r => r.situacaoAprovacao === 'ANA');
      setSelectedItems(selectable);
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (item: PurchaseRequestItem) => {
    const targetKey = getItemKey(item);
    const exists = selectedItems.some(s => getItemKey(s) === targetKey);
    if (exists) {
      setSelectedItems(selectedItems.filter(s => getItemKey(s) !== targetKey));
    } else {
      setSelectedItems([...selectedItems, item]);
    }
  };

  const handleOpenActionDialog = (items: PurchaseRequestItem[], action: 'APR' | 'REP') => {
    setCancelReason('');
    setCancelReasonError('');
    setActionTarget({ items, action });
  };

  const handleConfirmAction = async () => {
    if (!actionTarget || isProcessing) return;

    if (actionTarget.action === 'REP' && !cancelReason.trim()) {
      setCancelReasonError('O motivo do cancelamento / reprovação é obrigatório.');
      return;
    }

    const targetItems = [...actionTarget.items];
    const targetAction = actionTarget.action;
    const targetReason = cancelReason.trim();
    setIsProcessing(true);

    try {
      const { data } = await api.post('erp/purchases/requests/process-approval', {
        items: targetItems,
        action: targetAction,
        reason: targetAction === 'REP' ? targetReason : undefined,
      });

      let itemResults: ProcessApprovalResult[] = [];

      if (Array.isArray(data?.results) && data.results.length > 0) {
        itemResults = data.results.map((r: any, idx: number) => {
          const originalItem =
            targetItems.find(it => it.solicitacao === r.solicitacao && it.sequencia === r.sequencia) ||
            targetItems[idx];

          return {
            empresa: r.empresa ?? originalItem?.empresa,
            filial: r.filial ?? originalItem?.filial,
            solicitacao: r.solicitacao ?? originalItem?.solicitacao,
            sequencia: r.sequencia ?? originalItem?.sequencia,
            produtoOuServico: originalItem?.produto || originalItem?.servico || r.produtoOuServico,
            descricao: originalItem?.descricaoProduto || originalItem?.descricaoServico || r.descricao,
            status: r.status === 'error' ? 'error' : 'success',
            message:
              r.message ||
              (r.status === 'error'
                ? 'Erro ao processar item no ERP Sapiens.'
                : `Item ${targetAction === 'APR' ? 'aprovado' : 'reprovado'} com sucesso.`),
          };
        });
      } else {
        itemResults = targetItems.map(it => ({
          empresa: it.empresa,
          filial: it.filial,
          solicitacao: it.solicitacao,
          sequencia: it.sequencia,
          produtoOuServico: it.produto || it.servico,
          descricao: it.descricaoProduto || it.descricaoServico,
          status: 'success',
          message:
            data?.message || `Solicitação ${targetAction === 'APR' ? 'aprovada' : 'reprovada'} com sucesso no ERP.`,
        }));
      }

      setActionTarget(null);
      setCancelReason('');
      setCancelReasonError('');
      setResultModal({
        open: true,
        action: targetAction,
        results: itemResults,
      });

      await executeFilterSearch();
    } catch (error: any) {
      console.error('Erro ao processar solicitação:', error);
      const status = error?.response?.status;
      const msg = error?.response?.data?.message || 'Erro ao processar a ação no ERP Sapiens.';

      if (
        status === 401 ||
        msg.toLowerCase().includes('credenciais') ||
        msg.toLowerCase().includes('erp_login') ||
        msg.toLowerCase().includes('erp_password') ||
        msg.toLowerCase().includes('autentic')
      ) {
        setActionTarget(null);
        showNotification('Autenticação do ERP necessária. Informe suas credenciais.', 'error');
        setAuthModalOpen(true);
        return;
      }

      let itemResults: ProcessApprovalResult[] = [];

      if (Array.isArray(error?.response?.data?.results) && error.response.data.results.length > 0) {
        itemResults = error.response.data.results.map((r: any, idx: number) => {
          const originalItem =
            targetItems.find(it => it.solicitacao === r.solicitacao && it.sequencia === r.sequencia) ||
            targetItems[idx];

          return {
            empresa: r.empresa ?? originalItem?.empresa,
            filial: r.filial ?? originalItem?.filial,
            solicitacao: r.solicitacao ?? originalItem?.solicitacao,
            sequencia: r.sequencia ?? originalItem?.sequencia,
            produtoOuServico: originalItem?.produto || originalItem?.servico || r.produtoOuServico,
            descricao: originalItem?.descricaoProduto || originalItem?.descricaoServico || r.descricao,
            status: 'error',
            message: r.message || 'Inconsistência retornada pelo ERP Sapiens.',
          };
        });
      } else {
        const errorParts = msg
          .split('|')
          .map((s: string) => s.trim())
          .filter(Boolean);

        itemResults = targetItems.map((it, idx) => {
          const matchedError = errorParts.find(
            (p: string) =>
              p.includes(`#${it.solicitacao}`) ||
              p.includes(`Solicitação #${it.solicitacao}`) ||
              p.includes(`${it.solicitacao}`),
          );

          if (matchedError) {
            const cleanMsg = matchedError
              .replace(/^Erro ERP Sapiens:\s*/i, '')
              .replace(/^Item \d+:\s*/i, '')
              .trim();

            return {
              empresa: it.empresa,
              filial: it.filial,
              solicitacao: it.solicitacao,
              sequencia: it.sequencia,
              produtoOuServico: it.produto || it.servico,
              descricao: it.descricaoProduto || it.descricaoServico,
              status: 'error',
              message: cleanMsg,
            };
          }

          if (msg.startsWith('Parcial:') && !matchedError) {
            return {
              empresa: it.empresa,
              filial: it.filial,
              solicitacao: it.solicitacao,
              sequencia: it.sequencia,
              produtoOuServico: it.produto || it.servico,
              descricao: it.descricaoProduto || it.descricaoServico,
              status: 'success',
              message: `Item ${targetAction === 'APR' ? 'aprovado' : 'reprovado'} com sucesso.`,
            };
          }

          const fallbackErr = errorParts[idx] || errorParts[0] || msg;
          return {
            empresa: it.empresa,
            filial: it.filial,
            solicitacao: it.solicitacao,
            sequencia: it.sequencia,
            produtoOuServico: it.produto || it.servico,
            descricao: it.descricaoProduto || it.descricaoServico,
            status: 'error',
            message: fallbackErr
              .replace(/^Erro ERP Sapiens:\s*/i, '')
              .replace(/^Item \d+:\s*/i, '')
              .trim(),
          };
        });
      }

      setActionTarget(null);
      setResultModal({
        open: true,
        action: targetAction,
        results: itemResults,
      });

      await executeFilterSearch();
    } finally {
      setIsProcessing(false);
    }
  };

  const formatMinutesToTime = (minutes?: number) => {
    if (minutes === undefined || minutes === null) return '';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';

    const trimmed = dateStr.trim();

    if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
      return trimmed;
    }

    if (trimmed.includes('T')) {
      const d = new Date(trimmed);
      if (!Number.isNaN(d.getTime())) {
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
      }
    }

    const cleanDate = trimmed.split('T')[0];
    const parts = cleanDate.split('-');
    if (parts.length === 3) {
      const [year, month, day] = parts;
      return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
    }

    return dateStr;
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'APR':
        return (
          <Chip icon={<CheckCircle />} label="Aprovada" color="success" size="small" sx={{ fontWeight: 'bold' }} />
        );
      case 'REP':
        return <Chip icon={<Cancel />} label="Reprovada" color="error" size="small" sx={{ fontWeight: 'bold' }} />;
      case 'CAN':
        return (
          <Chip
            icon={<RemoveCircleOutline />}
            label="Cancelada"
            color="error"
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
        );
      default:
        return (
          <Chip icon={<HourglassTop />} label="Pendente" color="warning" size="small" sx={{ fontWeight: 'bold' }} />
        );
    }
  };

  return (
    <Box pb={3}>
      {/* CABEÇALHO COM TÍTULO E BOTÕES DE NAVEGAÇÃO ALINHADOS À DIREITA */}
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={1.5}>
        <Box display="flex" alignItems="center" gap={1}>
          <Rule color="inherit" fontSize="large" />
          <Typography variant="h5" fontWeight="bold" color="text.primary">
            Aprovação de Solicitações de Compra
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ContentPasteSearch />}
            onClick={() => handleNavigate('erp/purchases/purchase/search')}
            sx={{ fontWeight: 'bold', textTransform: 'none', height: 38 }}
          >
            Consultar Solicitações
          </Button>
          <Button
            variant="outlined"
            color="success"
            startIcon={<AddShoppingCart />}
            onClick={() => handleNavigate('erp/purchases/purchase/request')}
            sx={{ fontWeight: 'bold', textTransform: 'none', height: 38 }}
          >
            Gerar Solicitação
          </Button>
        </Stack>
      </Box>

      <Typography variant="body2" color="text.secondary" mb={3}>
        Consulte e analise pendências de aprovação das solicitações de compra enviadas via ERP Senior.
      </Typography>

      {/* PAINEL DE FILTROS */}
      <Paper elevation={2} sx={{ p: 2.5, mb: 3, borderRadius: 2, bgcolor: 'background.paper' }}>
        <Box display="flex" alignItems="center" gap={1} mb={2.5}>
          <FilterList color="primary" fontSize="small" />
          <Typography variant="subtitle2" fontWeight="bold" color="primary">
            Filtro
          </Typography>
        </Box>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="select-company-label">Empresa ERP</InputLabel>
              <Select
                labelId="select-company-label"
                value={companyId}
                label="Empresa ERP"
                onChange={handleCompanyChange}
                disabled={isLoadingCompanies}
              >
                {companyList.map(emp => (
                  <MenuItem key={emp.id} value={emp.id}>
                    [{emp.id}] {emp.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="select-branch-label">Filial</InputLabel>
              <Select
                labelId="select-branch-label"
                value={branchId}
                label="Filial"
                onChange={e => {
                  setBranchId(Number(e.target.value));
                  handleResetSearchData();
                }}
                disabled={isLoadingCompanies || !currentCompany.filiais.length}
              >
                {currentCompany.filiais?.map(fil => (
                  <MenuItem key={fil.id} value={fil.id}>
                    [{fil.id}] {fil.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="select-status-label">Situação</InputLabel>
              <Select
                labelId="select-status-label"
                value={statusFilter}
                label="Situação"
                onChange={e => {
                  setStatusFilter(e.target.value);
                  handleResetSearchData();
                }}
              >
                <MenuItem value="ANA">Pendentes</MenuItem>
                <MenuItem value="APR">Aprovadas</MenuItem>
                <MenuItem value="REP">Reprovadas</MenuItem>
                <MenuItem value="ALL">Todas</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Nº da Solicitação"
              placeholder="Ex: 19575, 19580-19590"
              value={purchaseRequestNum}
              onChange={e => {
                setPurchaseRequestNum(e.target.value);
                handleResetSearchData();
              }}
              InputProps={{
                endAdornment: purchaseRequestNum ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setPurchaseRequestNum('');
                        handleResetSearchData();
                      }}
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <GenericSearchInput
              label="Solicitante(s) ERP"
              source="erpUsers"
              value={requesterInput}
              onChange={val => {
                setRequesterInput(val);
                handleResetSearchData();
              }}
              multiple
              placeholder="Ex: 1, 2, 5-10"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <GenericSearchInput
              label="Produto ou Serviço"
              source="products"
              companyId={companyId}
              value={itemCodeInput}
              onChange={val => {
                setItemCodeInput(val);
                handleResetSearchData();
              }}
              multiple
              placeholder="Ex: 100.001, 100.002-100.005"
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Data Solicit. Inicial"
              type="date"
              value={startDate}
              onChange={e => {
                setStartDate(e.target.value);
                handleResetSearchData();
              }}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: startDate ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setStartDate('');
                        handleResetSearchData();
                      }}
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Data Solicit. Final"
              type="date"
              value={endDate}
              onChange={e => {
                setEndDate(e.target.value);
                handleResetSearchData();
              }}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: endDate ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEndDate('');
                        handleResetSearchData();
                      }}
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Filtro Rápido na Tela"
              placeholder="Digite para filtrar instantaneamente na tabela..."
              value={quickSearch}
              onChange={e => {
                setQuickSearch(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {quickSearch && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          setQuickSearch('');
                          setPage(0);
                        }}
                        sx={{ mr: 0.5 }}
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    )}
                    <Tooltip title="Filtro rápido sobre os registros já carregados na tela.">
                      <IconButton size="small" edge="end" color="primary">
                        <InfoOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => executeFilterSearch()}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <Search />}
              sx={{ height: '40px', fontWeight: 'bold', px: 4 }}
            >
              Pesquisar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* AÇÕES EM LOTE */}
      {selectedItems.length > 0 && (
        <Paper
          elevation={3}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            bgcolor: isDark ? alpha(theme.palette.primary.main, 0.2) : alpha(theme.palette.primary.light, 0.2),
            border: `1px solid ${theme.palette.primary.main}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
            {selectedItems.length} item(ns) selecionado(s) para ação em lote
          </Typography>
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
              onClick={() => handleOpenActionDialog(selectedItems, 'APR')}
              sx={{ fontWeight: 'bold' }}
            >
              Aprovar Selecionados
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<Cancel />}
              onClick={() => handleOpenActionDialog(selectedItems, 'REP')}
              sx={{ fontWeight: 'bold' }}
            >
              Reprovar Selecionados
            </Button>
          </Stack>
        </Paper>
      )}

      {/* TABELA DE SOLICITAÇÕES */}
      <Paper
        elevation={2}
        sx={{
          borderRadius: 2,
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box p={2} display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <List color="primary" fontSize="small" />
            <Typography variant="subtitle2" fontWeight="bold" color="primary">
              Solicitações ({filteredRequests.length})
            </Typography>
          </Box>
        </Box>

        <TableContainer sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow
                sx={{
                  '& th': {
                    bgcolor: isDark ? 'grey.900' : 'grey.100',
                    color: 'text.primary',
                    fontWeight: 'bold',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  },
                }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedItems.length > 0 &&
                      selectedItems.length < filteredRequests.filter(r => r.situacaoAprovacao === 'ANA').length
                    }
                    checked={
                      filteredRequests.filter(r => r.situacaoAprovacao === 'ANA').length > 0 &&
                      selectedItems.length === filteredRequests.filter(r => r.situacaoAprovacao === 'ANA').length
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>

                <TableCell>
                  <TableSortLabel
                    active={sortField === 'solicitacao'}
                    direction={sortField === 'solicitacao' ? sortOrder : 'asc'}
                    onClick={() => handleSortClick('solicitacao')}
                  >
                    Solicitação / Seq
                  </TableSortLabel>
                </TableCell>

                <TableCell>
                  <TableSortLabel
                    active={sortField === 'dataSolicitacao'}
                    direction={sortField === 'dataSolicitacao' ? sortOrder : 'asc'}
                    onClick={() => handleSortClick('dataSolicitacao')}
                  >
                    Data / Hora
                  </TableSortLabel>
                </TableCell>

                <TableCell>
                  <TableSortLabel
                    active={sortField === 'codUsuarioSolicitante'}
                    direction={sortField === 'codUsuarioSolicitante' ? sortOrder : 'asc'}
                    onClick={() => handleSortClick('codUsuarioSolicitante')}
                  >
                    Solicitante
                  </TableSortLabel>
                </TableCell>

                <TableCell>
                  <TableSortLabel
                    active={sortField === 'descricaoProduto'}
                    direction={sortField === 'descricaoProduto' ? sortOrder : 'asc'}
                    onClick={() => handleSortClick('descricaoProduto')}
                  >
                    Produto / Serviço
                  </TableSortLabel>
                </TableCell>

                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'quantidadeSolicitada'}
                    direction={sortField === 'quantidadeSolicitada' ? sortOrder : 'asc'}
                    onClick={() => handleSortClick('quantidadeSolicitada')}
                  >
                    Qtd. Solicitada
                  </TableSortLabel>
                </TableCell>

                <TableCell>
                  <TableSortLabel
                    active={sortField === 'centroCusto'}
                    direction={sortField === 'centroCusto' ? sortOrder : 'asc'}
                    onClick={() => handleSortClick('centroCusto')}
                  >
                    Centro de Custo
                  </TableSortLabel>
                </TableCell>

                <TableCell align="center">
                  <TableSortLabel
                    active={sortField === 'situacaoAprovacao'}
                    direction={sortField === 'situacaoAprovacao' ? sortOrder : 'asc'}
                    onClick={() => handleSortClick('situacaoAprovacao')}
                  >
                    Situação
                  </TableSortLabel>
                </TableCell>

                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      Carregando solicitações...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : !hasSearched && requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      gap={1}
                    >
                      <InfoOutlined /> Clique no botão <b>&quot;Pesquisar&quot;</b> para buscar as solicitações com os
                      filtros selecionados.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                    <Typography variant="body1" color="text.secondary">
                      Nenhuma solicitação de compra encontrada para os filtros aplicados.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRequests.map(row => {
                  const itemKey = getItemKey(row);
                  const isSelected = selectedItems.some(s => getItemKey(s) === itemKey);
                  const isPending = row.situacaoAprovacao === 'ANA';

                  return (
                    <TableRow
                      key={itemKey}
                      hover
                      selected={isSelected}
                      sx={{ '& td': { borderColor: theme.palette.divider } }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox checked={isSelected} disabled={!isPending} onChange={() => handleSelectItem(row)} />
                      </TableCell>

                      <TableCell sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <ShoppingCart fontSize="small" color="action" />
                          <HighlightText text={String(row.solicitacao)} highlight={quickSearch} />
                          {' / '}
                          <HighlightText text={String(row.sequencia)} highlight={quickSearch} />
                        </Box>
                      </TableCell>

                      <TableCell sx={{ color: 'text.secondary' }}>
                        {formatDate(row.dataSolicitacao)} {formatMinutesToTime(row.horaSolicitacao)}
                      </TableCell>

                      <TableCell sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                        <HighlightText text={String(row.codUsuarioSolicitante)} highlight={quickSearch} />
                        {' - '}
                        <HighlightText text={row.nomeUsuarioSolicitante || 'Não informado'} highlight={quickSearch} />
                      </TableCell>

                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Chip
                            label={row.produtoServico === 'P' ? 'Produto' : 'Serviço'}
                            size="small"
                            color={row.produtoServico === 'P' ? 'primary' : 'secondary'}
                            variant="outlined"
                            sx={{ fontWeight: 'bold', height: 20, fontSize: '0.7rem' }}
                          />
                          <Typography variant="body2" fontWeight="bold" color="text.primary">
                            [<HighlightText text={row.produto || row.servico || ''} highlight={quickSearch} />]{' '}
                            <HighlightText
                              text={row.descricaoProduto || row.descricaoServico || ''}
                              highlight={quickSearch}
                            />
                          </Typography>
                        </Box>
                        {row.complementoProduto && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Comp: {row.complementoProduto}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatNumberPtBR(row.quantidadeSolicitada ?? row.quantidade)}
                      </TableCell>

                      <TableCell sx={{ color: 'text.secondary' }}>
                        {row.centroCusto ? (
                          <>
                            <HighlightText text={row.centroCusto} highlight={quickSearch} />
                            {' - '}
                            <HighlightText text={row.descricaoCentroCusto || ''} highlight={quickSearch} />
                          </>
                        ) : (
                          '-'
                        )}
                      </TableCell>

                      <TableCell align="center">{getStatusChip(row.situacaoAprovacao)}</TableCell>

                      <TableCell align="center">
                        <Box display="flex" justifyContent="center" gap={0.5}>
                          <Tooltip title="Detalhes & Estoque">
                            <IconButton size="small" onClick={() => setDetailItem(row)}>
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {isPending && (
                            <>
                              <Tooltip title="Aprovar">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => handleOpenActionDialog([row], 'APR')}
                                >
                                  <CheckCircle fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Reprovar">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleOpenActionDialog([row], 'REP')}
                                >
                                  <Cancel fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, bgcolor: 'background.paper' }}>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredRequests.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={e => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage="Itens por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        </Box>
      </Paper>

      <PurchaseRequestDetailsModal
        open={Boolean(detailItem)}
        onClose={() => setDetailItem(null)}
        item={detailItem}
        onObservationUpdated={newObs => {
          if (detailItem) {
            setDetailItem({ ...detailItem, observacaoSolicitacao: newObs });
          }
          executeFilterSearch();
        }}
      />

      {/* 1. DIALOG CONFIRMAÇÃO DE AÇÃO */}
      <Dialog
        open={Boolean(actionTarget)}
        onClose={(_, reason) => {
          if (!isProcessing) {
            setActionTarget(null);
            setCancelReason('');
            setCancelReasonError('');
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Confirmar {actionTarget?.action === 'APR' ? 'Aprovação' : 'Reprovação'} de Solicitação(ões)
        </DialogTitle>
        <DialogContent dividers>
          {isProcessing ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                mb: 2,
                borderRadius: 1.5,
                bgcolor: isDark ? 'grey.800' : 'info.50',
                border: `1px solid ${theme.palette.info.main}`,
              }}
            >
              <CircularProgress size={24} color="info" />
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                  Processando no ERP Senior Sapiens...
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Aguarde a resposta do Webservice. Os resultados serão exibidos em seguida.
                </Typography>
              </Box>
            </Box>
          ) : (
            <DialogContentText color="text.secondary" mb={2}>
              Você está prestes a <b>{actionTarget?.action === 'APR' ? 'aprovar' : 'reprovar'}</b> o(s) seguinte(s){' '}
              <b>{actionTarget?.items.length}</b> item(ns) no ERP Senior:
            </DialogContentText>
          )}

          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ borderRadius: 1.5, maxHeight: 220, overflowY: 'auto', opacity: isProcessing ? 0.6 : 1 }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: isDark ? 'grey.900' : 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Emp / Fil</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Sol. / Seq</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Produto / Serviço</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Solicitante</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    Qtd
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {actionTarget?.items.map(item => (
                  <TableRow key={`${item.empresa}-${item.solicitacao}-${item.sequencia}`}>
                    <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {item.empresa} / {item.filial}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      {item.solicitacao} / {item.sequencia}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        [{item.produto || item.servico}]
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap display="block" maxWidth={180}>
                        {item.descricaoProduto || item.descricaoServico}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>
                      {item.codUsuarioSolicitante} - {item.nomeUsuarioSolicitante || 'N/A'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatNumberPtBR(item.quantidadeSolicitada ?? item.quantidade)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {actionTarget?.action === 'REP' && (
            <Box mt={2.5}>
              <TextField
                fullWidth
                multiline
                rows={3}
                size="small"
                required
                label="Motivo do Cancelamento / Reprovação *"
                placeholder="Descreva detalhadamente a justificativa para registrar no ERP Sapiens e notificar o solicitante..."
                value={cancelReason}
                error={Boolean(cancelReasonError)}
                helperText={cancelReasonError || `${cancelReason.length}/250 caracteres`}
                inputProps={{ maxLength: 250 }}
                onChange={e => {
                  setCancelReason(e.target.value);
                  if (e.target.value.trim()) setCancelReasonError('');
                }}
                disabled={isProcessing}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setActionTarget(null);
              setCancelReason('');
              setCancelReasonError('');
            }}
            color="inherit"
            disabled={isProcessing}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmAction}
            color={actionTarget?.action === 'APR' ? 'success' : 'error'}
            variant="contained"
            disabled={isProcessing}
            startIcon={isProcessing ? <CircularProgress size={18} color="inherit" /> : <AssignmentTurnedIn />}
            sx={{ fontWeight: 'bold' }}
          >
            {isProcessing
              ? 'Processando...'
              : `Confirmar ${actionTarget?.action === 'APR' ? 'Aprovação' : 'Reprovação'}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 2. MODAL TABULAR DE RESULTADOS */}
      <Dialog
        open={resultModal.open}
        onClose={() => setResultModal(prev => ({ ...prev, open: false }))}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'primary.main',
          }}
        >
          <AssignmentTurnedIn /> Retorno do Processamento de {resultModal.action === 'APR' ? 'Aprovação' : 'Reprovação'}{' '}
          (ERP Sapiens)
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2 }}>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 380 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ '& th': { bgcolor: isDark ? 'grey.900' : 'grey.200', fontWeight: 'bold' } }}>
                  <TableCell width={90}>Emp / Fil</TableCell>
                  <TableCell width={120}>Sol. / Seq</TableCell>
                  <TableCell>Produto / Serviço</TableCell>
                  <TableCell width={110}>Status</TableCell>
                  <TableCell>Mensagem / Detalhamento ERP</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {resultModal.results.map((resItem, idx) => (
                  <TableRow key={`${resItem.solicitacao}-${resItem.sequencia}-${idx}`} hover>
                    <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {resItem.empresa} / {resItem.filial}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      {resItem.solicitacao} / {resItem.sequencia}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        [{resItem.produtoOuServico}]
                      </Typography>
                      {resItem.descricao && (
                        <Typography variant="caption" color="text.secondary" noWrap display="block" maxWidth={220}>
                          {resItem.descricao}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={resItem.status === 'success' ? 'Sucesso' : 'Erro'}
                        size="small"
                        color={resItem.status === 'success' ? 'success' : 'error'}
                        sx={{ fontWeight: 'bold', height: 22, fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        color={resItem.status === 'success' ? 'text.secondary' : 'error.main'}
                        fontWeight={resItem.status === 'error' ? 'bold' : 'normal'}
                        sx={{ lineHeight: 1.35, display: 'block' }}
                      >
                        {resItem.message}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary">
            * A lista de solicitações foi atualizada após o processamento.
          </Typography>
          <Button
            onClick={() => setResultModal(prev => ({ ...prev, open: false }))}
            variant="contained"
            color="primary"
            sx={{ fontWeight: 'bold', px: 3 }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <ErpAuthModal
        open={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        onSuccess={() => {
          showNotification('Credenciais autenticadas com sucesso!', 'success');
          executeFilterSearch();
        }}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={notification.type} variant="filled" sx={{ fontWeight: 'bold' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
